import React, { useState, useEffect, useRef, useCallback } from "react";
import DischargeAdvanceModal from "./DischargeAdvanceModal";
import DischargeServicesModal from "./DischargeServicesModal";
import Select from "react-select";
// Make sure to adjust the path to your actual component
import DiscountAuthorityDropdown from '../components/DiscountAuthorityDropdown';

const DischargeEditModal = ({ show, data, onClose, onSave }) => {
  const [formData, setFormData] = useState(data || {});
  const [discountType, setDiscountType] = useState("amount");
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    setFormData(data || {});
    setDiscountType(data?.discountType || "amount");
  }, [data]);

  // Auto-focus first input
  useEffect(() => {
    if (show && formRef.current) {
      const firstInput = formRef.current.querySelector('input:not([readonly]):not([type="radio"]), select');
      firstInput?.focus();
    }
  }, [show]);

  // Calculate payable and due
  useEffect(() => {
    const totalAmount = Number(formData.totalAmount) || 0;
    const discountValue = Number(formData.discount) || 0;
    const received = Number(formData.received) || 0;

    let calculatedDiscount = 0;
    if (discountType === "percent") {
      calculatedDiscount = (totalAmount * discountValue) / 100;
    } else {
      calculatedDiscount = discountValue;
    }

    const payableAmount = totalAmount - calculatedDiscount;
    const dueAmount = payableAmount - received;

    setFormData((prev) => ({
      ...prev,
      discount: discountValue,
      payable: payableAmount >= 0 ? +payableAmount.toFixed(2) : 0,
      due: dueAmount >= 0 ? +dueAmount.toFixed(2) : 0,
    }));
  }, [formData.totalAmount, formData.discount, formData.received, discountType]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "discountType") {
      setDiscountType(value);
      setFormData((prev) => ({ ...prev, discount: 0 }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    // Prevent 'Enter' from opening dropdowns repeatedly if focused inside
    if (e.target.closest('.react-select__menu') || e.target.closest('.react-select__input-container')) return;

    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit();
      return;
    }

    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (show && formRef.current && e.target.closest('.edit-modal-container') === formRef.current.closest('.edit-modal-container')) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown, show]);

const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Validation: Agar discount diya hai toh authority zaruri hai
    if (formData.discount && Number(formData.discount) > 0 && !formData.disAuthority) {
      alert("Discount diya gaya hai, isliye Discount Authority select karna zaroori hai.");
      return;
    }

    // 2. Calculation: API ko bhejne se pehle exact Rupee (₹) value calculate karein
    let finalDiscountRupees = 0;
    const totalAmt = Number(formData.totalAmount) || 0;
    
    // formData.discount me user ka type kiya hua number hai (jaise 10 ya 500)
    const inputDiscountValue = Number(formData.discount) || 0; 

    if (discountType === "percent") {
      // Agar percent hai, toh total amount ka utna percent nikaliye
      finalDiscountRupees = (totalAmt * inputDiscountValue) / 100;
    } else {
      // Agar amount hai, toh direct wahi number le lijiye
      finalDiscountRupees = inputDiscountValue;
    }

    // 3. Payload: Purane formData ke upar naya calculated discount over-write karein
    const payloadToSave = {
      ...formData,
      discount: finalDiscountRupees, // ✅ Yahan fix apply ho gaya hai
    };

    // 4. API call trigger karne ke liye parent ko bhejein
    onSave(payloadToSave);
  };

  if (!show) return null;

  // ✅ DYNAMIC STYLES FOR REACT SELECT
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px', 
      fontSize: '13px',
      borderRadius: '6px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
      borderWidth: '1.5px'
    }),
    menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '6px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      padding: '8px 12px',
      backgroundColor: state.isSelected 
        ? 'var(--theme-color, #0088cc)' 
        : state.isFocused 
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
          : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
    }),
    placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '13px' })
  };

  const discountOptions = [
    { value: "amount", label: "Amount (₹)" },
    { value: "percent", label: "Percent (%)" }
  ];

  return (
    <>
      <div className="edit-modal-overlay" onClick={onClose}>
        <div className="edit-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="edit-modal-header">
            <div className="header-left">
              <i className="bi bi-pencil-square"></i>
              <span>Edit Discharge Record</span>
            </div>
            <button className="btn-close" onClick={onClose} type="button">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="edit-modal-body">

            <div className="action-row mb-3">
               <button 
                 type="button" 
                 className="btn-save"
                 onClick={() => setShowServicesModal(true)}
               >
                 <i className="bi bi-list-check"></i> Edit Services
               </button>
               <button 
                 type="button" 
                 className="btn-save"
                 onClick={() => setShowAdvanceModal(true)}
               >
                 <i className="bi bi-cash-stack"></i> Edit Advance
               </button>
            </div>

            {/* Date & Status */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Bill Date</label>
                <input
                  name="invDates"
                  type="date"
                  value={formData.invDates?.split('T')[0] || ""}
                  onChange={handleChange}
                  className="form-input"
                  tabIndex={1}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Discharge Date</label>
                <input
                  name="disdate"
                  type="date"
                  value={formData.disdate?.split('T')[0] || ""}
                  onChange={handleChange}
                  className="form-input"
                  tabIndex={2}
                />
              </div>
            </div>

            {/* Patient Status */}
            <div className="form-group">
              <label className="form-label">Patient Status</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="patDieOrLive"
                    checked={!formData.patDieOrLive}
                    onChange={() => setFormData(prev => ({ ...prev, patDieOrLive: false }))}
                    tabIndex={3}
                  />
                  <span className="radio-text alive">Alive</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="patDieOrLive"
                    checked={!!formData.patDieOrLive}
                    onChange={() => setFormData(prev => ({ ...prev, patDieOrLive: true }))}
                    tabIndex={4}
                  />
                  <span className="radio-text dead">Deceased</span>
                </label>
              </div>
            </div>

            {/* Remarks */}
            <div className="form-group">
              <label className="form-label">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks || ""}
                onChange={handleChange}
                className="form-textarea"
                rows={2}
                placeholder="Additional notes..."
                tabIndex={5}
              />
            </div>

            {/* Billing Row 1 */}
            <div className="form-row four-col">
              <div className="form-group">
                <label className="form-label">Total Amount</label>
                <input
                  name="totalAmount"
                  type="number"
                  value={formData.totalAmount || 0}
                  className="form-input readonly"
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Discount Type</label>
                <Select
                  options={discountOptions}
                  value={discountOptions.find(opt => opt.value === discountType)}
                  onChange={(selectedOption) => {
                    const newVal = selectedOption ? selectedOption.value : "amount";
                    setDiscountType(newVal);
                    setFormData((prev) => ({ ...prev, discount: 0 }));
                  }}
                  styles={selectStyles}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  tabIndex={6}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Discount</label>
                <input
                  name="discount"
                  type="number"
                  value={formData.discount || 0}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  tabIndex={7}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Payable</label>
                <input
                  name="payable"
                  type="number"
                  value={formData.payable || 0}
                  readOnly
                  className="form-input readonly payable"
                />
              </div>
            </div>

            {/* Billing Row 2 & Authority */}
            <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              
              <div className="form-group">
                <label className="form-label">Received</label>
                <input
                  name="received"
                  type="number"
                  value={formData.received || 0}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  tabIndex={8}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Due</label>
                <input
                  name="due"
                  type="number"
                  value={formData.due || 0}
                  readOnly
                  className="form-input readonly due"
                />
              </div>

               {/* ✅ NAYA FIELD: Discount Authority */}
               <div className="form-group">
                <label className="form-label">Dis. Authority</label>
                <DiscountAuthorityDropdown
                  value={formData.disAuthority}
                  onChange={(selectedOption) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      disAuthority: selectedOption ? selectedOption.value : "" 
                    }));
                  }}
                  styles={selectStyles}
                  className="form-input-dropdown"
                  tabIndex={9}
                />
              </div>
            </div>

            {/* Prepared By Row */}
            <div className="form-group" style={{ marginTop: '10px' }}>
                <label className="form-label">Prepared By</label>
                <input
                  name="docPreparedby"
                  type="text"
                  value={formData.docPreparedby || ""}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter name..."
                  tabIndex={10}
                />
              </div>

            {/* Footer Buttons */}
            <div className="modal-footer">
              <button className="btn-cancel" type="button" onClick={onClose} tabIndex={12}>
                Cancel
              </button>
              <button className="btn-save" type="submit" tabIndex={11}>
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>

      {showAdvanceModal && (
        <DischargeAdvanceModal 
          show={showAdvanceModal}
          ipdId={formData.ipD_ID || formData.ipdid}
          onClose={() => setShowAdvanceModal(false)}
        />
      )}

      {showServicesModal && (
        <DischargeServicesModal 
          show={showServicesModal}
          ipdId={formData.ipD_ID || formData.ipdid}
          onClose={() => setShowServicesModal(false)}
        />
      )}

      <style jsx>{`
        .edit-modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(3px);
          z-index: 9999; display: flex; align-items: center; justify-content: center;
          padding: 20px; animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        .edit-modal-container {
          background: white; border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-width: 750px; width: 100%; max-height: 90vh;
          overflow: hidden; animation: slideUp 0.3s ease;
        }

        .edit-modal-header {
          background: var(--theme-color, #0088cc);
          padding: 12px 16px;
          display: flex; justify-content: space-between; align-items: center;
          border-bottom: 2px solid rgba(0,0,0,0.1);
        }

        .header-left {
          display: flex; align-items: center; gap: 8px;
          color: white; font-size: 15px; font-weight: 600;
        }

        .header-left i { font-size: 16px; }

        .btn-close {
          width: 28px; height: 28px;
          background: rgba(255, 255, 255, 0.2);
          border: none; border-radius: 4px; color: white;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all 0.2s;
        }

        .btn-close:hover {
          background: rgba(255, 255, 255, 0.3); transform: rotate(90deg);
        }

        .edit-modal-body {
          padding: 16px; overflow-y: auto;
          max-height: calc(90vh - 100px);
        }

        .action-row { display: flex; gap: 10px; }

        .form-row {
          display: grid; grid-template-columns: repeat(2, 1fr);
          gap: 10px; margin-bottom: 10px;
        }

        .form-row.four-col { grid-template-columns: repeat(4, 1fr); }

        .form-group { display: flex; flex-direction: column; }
        .form-group.flex-2 { grid-column: span 2; }

        .form-label {
          font-size: 12px; font-weight: 600; color: #2c3e50; margin-bottom: 4px;
        }

        .form-input, .form-select, .form-textarea, .form-input-dropdown {
          padding: 6px 10px; border: 1.5px solid #cbd5e0;
          border-radius: 6px; font-size: 13px; color: #2c3e50;
          transition: all 0.2s; outline: none; width: 100%;
        }
        
        /* Remove padding for wrapper to fix size */
        .form-input-dropdown {
          padding: 0;
          border: none;
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%);
        }

        .form-input.readonly {
          background: #f3f4f6; color: #6c757d; cursor: not-allowed;
        }

        .form-input.payable { font-weight: 600; color: #27ae60; }
        .form-input.due { font-weight: 600; color: #e74c3c; }

        .form-textarea { resize: vertical; min-height: 50px; font-family: inherit; }

        /* Radio Group */
        .radio-group { display: flex; gap: 10px; }

        .radio-label {
          flex: 1; display: flex; align-items: center; gap: 6px;
          padding: 8px 12px; border: 1.5px solid #cbd5e0;
          border-radius: 6px; cursor: pointer; transition: all 0.2s;
          background: white;
        }

        .radio-label:hover { border-color: var(--theme-color, #0088cc); }

        .radio-label input[type="radio"] { margin: 0; cursor: pointer; }
        .radio-text { font-size: 13px; font-weight: 500; }

        .radio-label:has(input:checked) {
          border-color: var(--theme-color, #0088cc); border-width: 2px;
        }

        .radio-label:has(input:checked) .radio-text.alive { color: #27ae60; font-weight: 600; }
        .radio-label:has(input:checked) .radio-text.dead { color: #e74c3c; font-weight: 600; }

        /* Footer */
        .modal-footer {
          display: flex; justify-content: flex-end; gap: 10px;
          margin-top: 16px; padding-top: 16px; border-top: 1.5px solid #e0e6ed;
        }

        .btn-cancel, .btn-save {
          padding: 8px 20px; border-radius: 6px; font-size: 13px; font-weight: 600;
          border: none; cursor: pointer; transition: all 0.2s;
          display: inline-flex; align-items: center; gap: 6px;
        }

        .btn-cancel { background: #e0e6ed; color: #2c3e50; }
        .btn-cancel:hover { background: #cbd5e0; }

        .btn-save {
          background: var(--theme-color, #0088cc); 
          color: white;
        }

        .btn-save:hover {
          opacity: 0.9; transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
        }

        @media (max-width: 768px) {
          .form-row, .form-row.four-col { grid-template-columns: 1fr; }
          .form-group.flex-2 { grid-column: span 1; }
          .modal-footer { flex-direction: column-reverse; }
          .btn-cancel, .btn-save { width: 100%; justify-content: center; }
        }
      `}</style>
    </>
  );
};

export default DischargeEditModal;